import { createGroup, createRuleForColumn, DEFAULT_FILTER_COLUMN, getRuleColumn } from "./filterEditorFactory";
import { FilterColumn, FilterContext, FilterEditorRoot, FilterEntry, FilterItem, FilterRule } from "../types/filterEditor";
import { FilterLogicalOperator } from "../types/filterTree";


type RulePatch = Partial<Omit<FilterRule, "id" | "kind">>;

export type FilterEditorAction =
    /** Adds a row at `position` (0 = first), at the end when omitted. */
    | { type: "addRule"; context: FilterContext; position?: number; column?: FilterColumn }
    | { type: "addGroup"; position?: number }
    | { type: "addRuleToGroup"; groupId: string; context: FilterContext; column?: FilterColumn }
    | { type: "changeColumn"; id: string; column: FilterColumn; context: FilterContext }
    | { type: "updateRule"; id: string; patch: RulePatch }
    | { type: "updateGroup"; id: string; operator: FilterLogicalOperator }
    /** Operator between the item and the next one */
    | { type: "updateJoin"; id: string; join: FilterLogicalOperator }
    | { type: "remove"; id: string }
    | { type: "clear" }
    | { type: "replace"; root: FilterEditorRoot };

const insertAt = (root: FilterEditorRoot, item: FilterItem, position?: number): FilterEditorRoot => {
    const entry: FilterEntry = { join: "and", item };
    const index = position === undefined ? root.length : Math.max(0, Math.min(position, root.length));
    return [...root.slice(0, index), entry, ...root.slice(index)];
};

const mapRules = (root: FilterEditorRoot, update: (rule: FilterRule) => FilterRule): FilterEditorRoot =>
    root.map((entry) => {
        if (entry.item.kind === "group") {
            return { ...entry, item: { ...entry.item, rules: entry.item.rules.map(update) } };
        }
        return { ...entry, item: update(entry.item) };
    });

export const countRules = (root: FilterEditorRoot): number =>
    root.reduce((sum, entry) => sum + (entry.item.kind === "group" ? entry.item.rules.length : 1), 0);

export const filterEditorReducer = (root: FilterEditorRoot, action: FilterEditorAction): FilterEditorRoot => {
    switch (action.type) {
        case "addRule":
            return insertAt(
                root,
                createRuleForColumn(action.column ?? DEFAULT_FILTER_COLUMN, action.context),
                action.position
            );
        case "addGroup":
            return insertAt(root, createGroup("and"), action.position);
        case "addRuleToGroup":
            return root.map((entry) =>
                entry.item.kind === "group" && entry.item.id === action.groupId
                    ? {
                          ...entry,
                          item: {
                              ...entry.item,
                              rules: [
                                  ...entry.item.rules,
                                  createRuleForColumn(action.column ?? DEFAULT_FILTER_COLUMN, action.context),
                              ],
                          },
                      }
                    : entry
            );
        case "changeColumn":
            return mapRules(root, (rule) => {
                if (rule.id !== action.id || getRuleColumn(rule) === action.column) {
                    return rule;
                }
                return { ...createRuleForColumn(action.column, action.context), id: rule.id };
            });
        case "updateRule":
            return mapRules(root, (rule) => (rule.id === action.id ? ({ ...rule, ...action.patch } as FilterRule) : rule));
        case "updateGroup":
            return root.map((entry) =>
                entry.item.kind === "group" && entry.item.id === action.id
                    ? { ...entry, item: { ...entry.item, operator: action.operator } }
                    : entry
            );
        case "updateJoin":
            return root.map((entry) => (entry.item.id === action.id ? { ...entry, join: action.join } : entry));
        case "remove":
            return root
                .filter((entry) => entry.item.id !== action.id)
                .map((entry) =>
                    entry.item.kind === "group"
                        ? { ...entry, item: { ...entry.item, rules: entry.item.rules.filter((rule) => rule.id !== action.id) } }
                        : entry
                );
        case "clear":
            return [];
        case "replace":
            return action.root;
    }
};
